/**
 * Helpers for the multi-facility admin dashboard (read-only slice).
 * Builds a flat, deduped facility list from nested user-facility trees and
 * derives hierarchy labels from facility_group_id / parent_facility_group_id.
 */

import type {
  FacilityStatusDashboardResponse,
  FacilityStatusMember,
  FacilityStatusRollup,
  UserFacility,
} from "@services/facilityManagement";

export type FacilityHierarchyRole = "parent" | "child" | "standalone" | "orphan_child";

export type DashboardFacilityFlat = UserFacility & {
  _fid: number;
  _facility_group_id: string;
  _parent_facility_group_id: string;
  hierarchy_role: FacilityHierarchyRole;
  /** Parent row's facility_group_id when this facility is grouped under PF-…. */
  _rollup_group_key: string | "";
  /** From facility-status-dashboard persisted enforcement snapshots (optional). */
  _enforcement_action_count?: number;
  _enforcement_total_amount?: number;
  _enforcement_last_captured?: string | null;
};

function normPid(value: unknown): string {
  if (typeof value !== "string") return "";
  return value.trim().toUpperCase();
}

export function dashboardFacilityNumericId(f: UserFacility): number | null {
  const raw = f.facility_id ?? f.id;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * DFS flatten of API trees (parents with nested child_facilities), deduped by facility id.
 */
export function flattenUserFacilitiesTrees(
  roots: UserFacility[],
): UserFacility[] {
  const seen = new Set<number>();
  const out: UserFacility[] = [];

  function walk(node: UserFacility): void {
    const fid = dashboardFacilityNumericId(node);
    if (fid === null || seen.has(fid)) return;
    seen.add(fid);
    out.push(node);
    const kids = Array.isArray(node.child_facilities)
      ? node.child_facilities
      : [];
    kids.forEach(walk);
  }

  roots.forEach(walk);
  return out;
}

/**
 * Decorate flat rows with hierarchy role and which PF-… group bucket they belong to for rollups.
 */
export function buildDashboardFacilityRows(
  flat: UserFacility[],
): DashboardFacilityFlat[] {
  /** True when some row declares itself parent for this PF-… share code. */
  const hasDeclaringParentRow = (gid: string): boolean =>
    flat.some((f) => normPid(f.facility_group_id) === gid && !normPid(f.parent_facility_group_id));

  const rows: DashboardFacilityFlat[] = flat.map((f) => {
    const fid = dashboardFacilityNumericId(f);
    const _fid = fid ?? -1;

    const ownGid = normPid(f.facility_group_id);
    const pgid = normPid(f.parent_facility_group_id);

    let hierarchy_role: FacilityHierarchyRole = "standalone";
    let rollup: string | "" = "";

    if (ownGid && !pgid) {
      hierarchy_role = "parent";
      rollup = ownGid;
    } else if (pgid) {
      const parentVisible = hasDeclaringParentRow(pgid);
      hierarchy_role = parentVisible ? "child" : "orphan_child";
      rollup = pgid;
    } else {
      rollup = "";
    }

    const enriched: DashboardFacilityFlat = {
      ...f,
      _fid,
      _facility_group_id: ownGid,
      _parent_facility_group_id: pgid,
      hierarchy_role,
      _rollup_group_key: rollup,
    };

    return enriched;
  });

  return rows.sort((a, b) =>
    `${a.facility_name ?? ""}`.localeCompare(`${b.facility_name ?? ""}`),
  );
}

export interface DashboardRollups {
  total: number;
  active: number;
  verified: number;
  inactive: number;
  unverified: number;
  profileNotApproved: number;
  medicareCertGap: number;
  childNeedingAttention: number;
  orphanChildren: number;
}

export function computeDashboardRollups(
  rows: DashboardFacilityFlat[],
): DashboardRollups {
  let active = 0;
  let verified = 0;
  let inactive = 0;
  let unverified = 0;
  let profileNotApproved = 0;
  let medicareCertGap = 0;
  let childNeedingAttention = 0;
  let orphanChildren = 0;

  for (const r of rows) {
    if (r.is_active !== false) active++;
    else inactive++;

    if (r.is_verified === true) verified++;
    else unverified++;

    const approved = Boolean((r as { is_profile_approved?: boolean }).is_profile_approved);
    if (!approved) profileNotApproved++;

    const req = Boolean((r as { requires_medicare_certification?: boolean }).requires_medicare_certification);
    const cert = (r as { medicare_certification_date?: string | null }).medicare_certification_date;
    if (req && !String(cert ?? "").trim()) medicareCertGap++;

    const childProblems =
      !approved ||
      r.is_verified !== true ||
      r.is_active === false ||
      (req && !String(cert ?? "").trim());
    if (r.hierarchy_role === "child" && childProblems) childNeedingAttention++;

    if (r.hierarchy_role === "orphan_child") orphanChildren++;
  }

  return {
    total: rows.length,
    active,
    verified,
    inactive,
    unverified,
    profileNotApproved,
    medicareCertGap,
    childNeedingAttention,
    orphanChildren,
  };
}

export function collectOrderedDashboardMembers(
  resp: FacilityStatusDashboardResponse,
): FacilityStatusMember[] {
  const out: FacilityStatusMember[] = [];
  const seen = new Set<number>();

  for (const g of resp.groups) {
    for (const m of g.members) {
      const fid = dashboardFacilityNumericId(m.facility);
      if (fid == null || seen.has(fid)) continue;
      seen.add(fid);
      out.push(m);
    }
  }
  for (const m of resp.standalone_members) {
    const fid = dashboardFacilityNumericId(m.facility);
    if (fid == null || seen.has(fid)) continue;
    seen.add(fid);
    out.push(m);
  }
  return out;
}

export function dashboardRowsWithStatusEnforcement(
  members: FacilityStatusMember[],
): DashboardFacilityFlat[] {
  const facilities = members.map((m) => m.facility);
  const baseRows = buildDashboardFacilityRows(facilities);
  const byId = new Map<number, FacilityStatusMember>();
  for (const m of members) {
    const id = dashboardFacilityNumericId(m.facility);
    if (id != null) byId.set(id, m);
  }
  return baseRows.map((row) => {
    const id = dashboardFacilityNumericId(row as UserFacility);
    const src = id != null ? byId.get(id) : undefined;
    if (!src) return row;
    return {
      ...row,
      hierarchy_role: src.hierarchy_role,
      _enforcement_action_count: src.enforcement.total_action_count,
      _enforcement_total_amount: src.enforcement.total_amount,
      _enforcement_last_captured: src.enforcement.last_captured_at ?? null,
    };
  });
}

export function rollupFromApiGlobal(
  r: FacilityStatusRollup,
): DashboardRollups {
  return {
    total: r.total_members,
    active: r.active,
    verified: r.verified,
    inactive: r.inactive,
    unverified: r.unverified,
    profileNotApproved: r.profile_not_approved,
    medicareCertGap: r.medicare_cert_gap,
    childNeedingAttention: r.child_needing_attention,
    orphanChildren: r.orphan_children,
  };
}
